import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { partnersApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { ErrorState, Skeleton } from '@/components/ui/Feedback'
import { FieldShell } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { cn } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { PartnerReport, PartnerReportRow } from '@/types/api'

import { daysAgo, isoDay, num, pct, usd } from './format'
import { Segmented, TD, TH, THEAD_ROW, TableScroll } from './ui'

type Preset = '7' | '30' | 'month' | 'custom'
const MONEY: Array<keyof PartnerReportRow> = ['grossCents', 'discountCents', 'paidCents', 'earnedCents', 'settledCents', 'pendingCents']
const COUNTS: Array<keyof PartnerReportRow> = ['couponsCreated', 'couponsActive', 'usersOnboarded']

function monthStart(): string {
  const now = new Date()
  return isoDay(new Date(now.getFullYear(), now.getMonth(), 1))
}

/** Every partner over a date range, with a CSV of the same. */
export function ReportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [preset, setPreset] = useState<Preset>('30')
  const [from, setFrom] = useState(daysAgo(29))
  const [to, setTo] = useState(isoDay())
  const [downloading, setDownloading] = useState(false)

  const choose = (next: Preset) => {
    setPreset(next)
    if (next === '7') {
      setFrom(daysAgo(6))
      setTo(isoDay())
    } else if (next === '30') {
      setFrom(daysAgo(29))
      setTo(isoDay())
    } else if (next === 'month') {
      setFrom(monthStart())
      setTo(isoDay())
    }
  }

  const rangeError = from && to && from > to ? 'The start is after the end.' : null
  const report = useQuery({
    queryKey: ['partners', 'report', from, to],
    queryFn: () => partnersApi.report(from, to),
    enabled: open && !!from && !!to && !rangeError,
  })

  const download = async () => {
    setDownloading(true)
    try {
      await partnersApi.reportCsv(from, to)
    } catch (error) {
      toast.error('Could not download the CSV', error instanceof ApiError ? error.message : undefined)
    } finally {
      setDownloading(false)
    }
  }

  const rows = report.data?.partners ?? []
  const totals = totalsOf(report.data)

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title="Partner report"
      description="Coupons, users onboarded and money for every partner, counted over the dates you pick."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button variant="secondary" loading={downloading} disabled={!!rangeError || !from || !to} onClick={() => void download()}>
            Download CSV
          </Button>
        </>
      }
    >
      <div className="space-y-4 pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1">
            <p className="label-eyebrow mb-2">Range</p>
            <Segmented
              label="Range"
              size="sm"
              value={preset}
              onChange={choose}
              options={[
                { value: '7', label: '7 days' },
                { value: '30', label: '30 days' },
                { value: 'month', label: 'This month' },
                { value: 'custom', label: 'Custom' },
              ]}
            />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:w-[300px]">
            <FieldShell label="From" htmlFor="report-from">
              <input
                id="report-from"
                type="date"
                value={from}
                max={to || undefined}
                onChange={(event) => {
                  setPreset('custom')
                  setFrom(event.target.value)
                }}
                className="field-box h-9 py-0 text-[13.5px]"
              />
            </FieldShell>
            <FieldShell label="To" htmlFor="report-to">
              <input
                id="report-to"
                type="date"
                value={to}
                min={from || undefined}
                onChange={(event) => {
                  setPreset('custom')
                  setTo(event.target.value)
                }}
                className="field-box h-9 py-0 text-[13.5px]"
              />
            </FieldShell>
          </div>
        </div>
        {rangeError && <p className="text-[13px] text-danger">{rangeError}</p>}

        <div className={cn('panel overflow-hidden transition-opacity', report.isFetching && !report.isLoading && 'opacity-60')}>
          {report.isLoading ? (
            <div className="space-y-2 p-4">
              {[0, 1, 2].map((index) => (
                <Skeleton key={index} className="h-9 w-full" />
              ))}
            </div>
          ) : report.isError ? (
            <ErrorState title="The report could not be loaded" description={report.error instanceof ApiError ? report.error.message : undefined} onRetry={() => void report.refetch()} />
          ) : rows.length === 0 ? (
            <p className="px-5 py-8 text-center text-[13.5px] text-ink-muted">No partners.</p>
          ) : (
            <TableScroll>
              <table className="w-full min-w-[860px] text-left text-[13px]">
                <thead>
                  <tr className={THEAD_ROW}>
                    <th className={TH}>Partner</th>
                    <th className={cn(TH, 'text-right')}>Max</th>
                    <th className={cn(TH, 'text-right')}>Codes</th>
                    <th className={cn(TH, 'text-right')}>Users</th>
                    <th className={cn(TH, 'text-right')}>List price</th>
                    <th className={cn(TH, 'text-right')}>Discount</th>
                    <th className={cn(TH, 'text-right')}>Paid</th>
                    <th className={cn(TH, 'text-right')}>Earned</th>
                    <th className={cn(TH, 'text-right')}>To settle</th>
                    <th className={cn(TH, 'text-right')}>Settled</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className={cn(TD, 'whitespace-nowrap')}>
                        <span className="font-medium text-ink">{row.name}</span>
                        <span className="num ml-1.5 text-[11.5px] text-ink-muted">{row.tag}</span>
                      </td>
                      <td className={cn(TD, 'num text-right')}>{pct(row.maxDiscountPct)}</td>
                      <td className={cn(TD, 'num text-right text-ink-soft')}>
                        {num(row.couponsActive)}
                        <span className="text-ink-faint">/{num(row.couponsCreated)}</span>
                      </td>
                      <td className={cn(TD, 'num text-right')}>{num(row.usersOnboarded)}</td>
                      <td className={cn(TD, 'num text-right text-ink-soft')}>{usd(row.grossCents)}</td>
                      <td className={cn(TD, 'num text-right text-ink-soft')}>{usd(row.discountCents)}</td>
                      <td className={cn(TD, 'num text-right')}>{usd(row.paidCents)}</td>
                      <td className={cn(TD, 'num text-right font-medium text-ink')}>{usd(row.earnedCents)}</td>
                      <td className={cn(TD, 'num text-right', num(row.pendingCents) > 0 ? 'text-warning' : 'text-ink-faint')}>{usd(row.pendingCents)}</td>
                      <td className={cn(TD, 'num text-right text-ink-soft')}>{usd(row.settledCents)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-line-strong bg-surface-muted font-semibold text-ink">
                    <td className={TD}>Total</td>
                    <td className={TD} />
                    <td className={cn(TD, 'num text-right')}>
                      {totals.couponsActive}
                      <span className="text-ink-faint">/{totals.couponsCreated}</span>
                    </td>
                    <td className={cn(TD, 'num text-right')}>{totals.usersOnboarded}</td>
                    <td className={cn(TD, 'num text-right')}>{usd(totals.grossCents)}</td>
                    <td className={cn(TD, 'num text-right')}>{usd(totals.discountCents)}</td>
                    <td className={cn(TD, 'num text-right')}>{usd(totals.paidCents)}</td>
                    <td className={cn(TD, 'num text-right')}>{usd(totals.earnedCents)}</td>
                    <td className={cn(TD, 'num text-right')}>{usd(totals.pendingCents)}</td>
                    <td className={cn(TD, 'num text-right')}>{usd(totals.settledCents)}</td>
                  </tr>
                </tfoot>
              </table>
            </TableScroll>
          )}
        </div>
        <p className="text-[12px] leading-relaxed text-ink-faint">
          Money is in US dollars. List price is before the coupon; earned is the partner's commission on it (void lines excluded).
        </p>
      </div>
    </Modal>
  )
}

/** The server's totals, filled in from the rows where it leaves one out. */
function totalsOf(report: PartnerReport | undefined): Record<string, number> {
  const out: Record<string, number> = {}
  for (const key of [...MONEY, ...COUNTS]) {
    const given = report?.totals?.[key as keyof PartnerReport['totals']]
    out[key] = given !== undefined && given !== null ? num(given) : (report?.partners ?? []).reduce((sum, row) => sum + num(row[key]), 0)
  }
  return out
}
